<template>
  <div class="module_scheme_slots">
    <div class="module_scheme_slots__bar">
      <span class="text-subtitle-2">Оборудование схемы</span>
      <v-spacer></v-spacer>
      <span v-if="groups.length" class="module_scheme_slots__info">{{ summary }}</span>
    </div>

    <!-- выбранный объект виден в поле «Объект» выше, поэтому дублируем его только когда объекта нет -->
    <div v-if="!idObject && (groups.length || orphans.length)" class="module_scheme_slots__object">
      Объект не выбран — купить оборудование нельзя
    </div>

    <div v-if="loading" class="module_scheme_slots__state">
      <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
    </div>
    <div v-else-if="error" class="module_scheme_slots__state">{{ error }}</div>
    <div v-else-if="!groups.length && !orphans.length" class="module_scheme_slots__state">
      В узлах схемы нет оборудования, к которому можно подобрать номенклатуру
    </div>

    <template v-else>
      <!-- выбор нескольких слотов: «все КШ» и т.п.; массово назначаем одну позицию -->
      <div class="module_scheme_slots__tools">
        <v-menu offset-y max-height="320">
          <template v-slot:activator="{ on, attrs }">
            <v-btn small text :color="activeColor" v-bind="attrs" v-on="on">
              <v-icon left small>mdi-checkbox-multiple-marked-outline</v-icon>
              Выбрать все…
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item v-for="type in typeOptions" :key="type.idElement" @click="selectType(type.idElement)">
              <v-list-item-content>
                <v-list-item-title>{{ type.title }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action-text class="ml-4">{{ type.count }}</v-list-item-action-text>
            </v-list-item>
            <v-list-item v-if="!typeOptions.length" disabled>
              <v-list-item-title>Нет слотов, доступных для выбора</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-spacer></v-spacer>
        <span class="module_scheme_slots__hint" :class="{ 'module_scheme_slots__hint--notice': notice }">
          {{ notice || "Shift+клик по элементу на схеме — добавить к выбору" }}
        </span>
      </div>

      <div v-if="selectedSlots.length" class="module_scheme_slots__actions">
        <div class="module_scheme_slots__actions_head">
          <span class="module_scheme_slots__actions_text">
            Выбрано <b>{{ selectedSlots.length }}</b> · {{ selectedSlots[0].elementName }}
          </span>
          <v-spacer></v-spacer>
          <v-btn icon x-small title="Снять выделение" @click="clearSelection">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="module_scheme_slots__actions_buttons">
          <!-- подсказка на обёртке: у disabled-кнопки (pointer-events: none) собственный title не показывается;
               отключённая кнопка рисуется обычным серым стилем, а не бледным фиолетовым -->
          <span :title="idObject ? '' : NO_OBJECT_TEXT">
            <v-btn
              x-small
              depressed
              :dark="canPick"
              :color="canPick ? activeColor : undefined"
              :disabled="!canPick"
              @click="openPicker(selectedSlots)"
            >
              Подобрать позицию
            </v-btn>
          </span>
          <v-btn
            x-small
            text
            :disabled="!canClear || saving"
            title="Убрать выбранную позицию у выделенных слотов"
            @click="clearSelectedChoices"
          >
            Убрать позицию
          </v-btn>
          <v-btn
            x-small
            text
            color="orange darken-3"
            :disabled="!canPurchase || saving"
            :title="purchaseHint"
            @click="purchaseDialog = true"
          >
            Куплено…
          </v-btn>
        </div>
      </div>

      <div class="module_scheme_slots__list">
        <div v-for="group in groups" :key="group.uid" class="mb-2">
          <div class="module_scheme_slots__group">
            <button
              type="button"
              class="module_scheme_slots__group_toggle"
              :aria-expanded="!isCollapsed(group.uid)"
              @click="toggleGroup(group.uid)"
            >
              <v-icon small class="mr-1">
                {{ isCollapsed(group.uid) ? "mdi-chevron-right" : "mdi-chevron-down" }}
              </v-icon>
              <span class="module_scheme_slots__group_title">{{ group.title }}</span>
            </button>
            <!-- вариант сборки узла (подпапки «2», «3» в Elements чертежа): картинка модуля на схеме и этот
                 список показывают выбранный вариант; сохраняется в layout схемы сразу -->
            <v-btn-toggle
              v-if="group.variants.length > 1"
              :value="group.variant"
              mandatory
              dense
              color="primary"
              class="module_scheme_slots__variants mr-2"
              :disabled="variantSaving"
              @change="$emit('set-variant', { uid: group.uid, variant: $event })"
            >
              <v-btn
                v-for="variant in group.variants"
                :key="`variant-${group.uid}-${variant}`"
                :value="variant"
                x-small
                text
                :title="`Вариант сборки ${variant}`"
              >
                {{ variant }}
              </v-btn>
            </v-btn-toggle>
            <span
              class="module_scheme_slots__group_count"
              :class="{ 'module_scheme_slots__group_count--done': group.slots.every((s) => s.chosen) }"
            >
              {{ group.slots.filter((s) => s.chosen).length }} / {{ group.slots.length }}
            </span>
          </div>
          <div v-show="!isCollapsed(group.uid)" class="mt-1">
            <div
              v-for="slot in group.slots"
              :key="slot.ref"
              class="module_scheme_slots__slot"
              :data-slot="`${slot.uid}:${slot.key}`"
              :class="[
                slot.chosen ? `module_scheme_slots__slot--${slot.status}` : 'module_scheme_slots__slot--empty',
                {
                  'module_scheme_slots__slot--selected': slot.selected,
                  'module_scheme_slots__slot--frozen': slot.frozen,
                  'module_scheme_slots__slot--hovered': isSlotHoveredFromSvg(slot.uid, slot.key),
                },
              ]"
              @mouseenter="$emit('hover-slot', { uid: slot.uid, key: slot.key, fromSvg: false })"
              @mouseleave="$emit('hover-slot', null)"
            >
              <span class="module_scheme_slots__check" :title="checkTitle(slot)">
                <v-icon v-if="slot.frozen" small color="grey darken-1">mdi-lock-outline</v-icon>
                <v-simple-checkbox
                  v-else
                  :value="slot.selected"
                  :disabled="isCheckDisabled(slot)"
                  :ripple="false"
                  :color="activeColor"
                  @input="toggleSelect(slot)"
                ></v-simple-checkbox>
              </span>
              <button type="button" class="module_scheme_slots__slot_main" @click="openSlot(slot)">
                <v-img
                  :src="slot.icon"
                  contain
                  width="24"
                  max-width="24"
                  height="24"
                  class="flex-grow-0 mr-2"
                ></v-img>
                <span class="module_scheme_slots__slot_text">
                  <span class="module_scheme_slots__slot_label">{{ slot.label }}</span>
                  <span v-if="slot.chosen" class="module_scheme_slots__slot_value">
                    {{ slot.chosen.name || "…" }}
                    <b v-if="slot.quantity > 1" class="module_scheme_slots__qty">× {{ slot.quantity }}</b>
                  </span>
                  <span v-else class="module_scheme_slots__slot_value module_scheme_slots__slot_value--empty">
                    Выбрать
                  </span>
                </span>
              </button>
              <!-- состояние справа, из поля status слота: не подобрано ○ → набросок с позицией ✓ → куплено → установлено -->
              <span class="module_scheme_slots__state" :title="stateTitle(slot)">
                <v-chip
                  v-if="slot.frozen"
                  x-small
                  label
                  :color="statusMeta(slot).color"
                  :text-color="statusMeta(slot).textColor"
                >
                  <v-icon left x-small>{{ slot.status === "installed" ? "mdi-check-all" : "mdi-check" }}</v-icon>
                  {{ statusMeta(slot).label }}
                </v-chip>
                <v-icon v-else-if="slot.chosen" :color="statusMeta(slot).stroke">mdi-check-circle</v-icon>
                <v-icon v-else color="grey lighten-1">mdi-circle-outline</v-icon>
              </span>
            </div>
          </div>
        </div>

        <!-- записи слотов, которых больше нет на чертежах (узел убрали или заменили SVG) -->
        <div v-if="orphans.length" class="mb-2">
          <div class="module_scheme_slots__group module_scheme_slots__group--static">
            <span class="module_scheme_slots__group_title">Нет на чертеже</span>
            <v-spacer></v-spacer>
            <span class="module_scheme_slots__group_count">{{ orphans.length }}</span>
          </div>
          <div class="mt-1">
            <div
              v-for="orphan in orphans"
              :key="orphan.ref"
              class="module_scheme_slots__slot module_scheme_slots__slot--orphan"
            >
              <span class="module_scheme_slots__check">
                <v-icon small color="orange darken-2">mdi-alert-outline</v-icon>
              </span>
              <button type="button" class="module_scheme_slots__slot_main" @click="openOrphan(orphan)">
                <span class="module_scheme_slots__slot_text">
                  <span class="module_scheme_slots__slot_label">{{ orphan.groupTitle }} · {{ orphan.label }}</span>
                  <span class="module_scheme_slots__slot_value">
                    {{ orphan.chosen ? orphan.chosen.name || "…" : "позиция не выбрана" }}
                    <b v-if="orphan.quantity > 1" class="module_scheme_slots__qty">× {{ orphan.quantity }}</b>
                  </span>
                </span>
              </button>
              <v-chip
                v-if="orphan.frozen"
                x-small
                label
                :color="statusMeta(orphan).color"
                :text-color="statusMeta(orphan).textColor"
              >
                {{ statusMeta(orphan).label }}
              </v-chip>
              <v-btn v-else icon x-small title="Удалить слот" :disabled="saving" @click="deleteOrphan(orphan)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </template>

    <ModuleSchemeSlotPicker
      v-model="pickerOpen"
      :targets="pickerTargets"
      :saving="saving"
      @attach="onAttach"
      @clear="onClear"
    />
    <ModuleSchemeSlotDetails v-model="detailsOpen" :row="detailsSlot" :object-name="detailsObjectName" />

    <v-dialog v-model="purchaseDialog" max-width="460">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-medium">
          Отметить купленным: {{ pluralSlots(selectedSlots.length) }}?
        </v-card-title>
        <v-card-text class="text-body-2">
          Позиции и цены зафиксируются на сегодня, а оборудование закрепится за объектом
          <b>«{{ objectName || `№ ${idObject}` }}»</b>. После этого позицию, количество, условия и объект
          слотов изменить нельзя — только статус.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="purchaseDialog = false">Отмена</v-btn>
          <v-btn color="orange darken-3" text :loading="saving" @click="purchase">Отметить купленными</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ModuleSchemeSlotPicker from "@/components/module-schemes/ModuleSchemeSlotPicker";
import ModuleSchemeSlotDetails from "@/components/module-schemes/ModuleSchemeSlotDetails";
import {
  ACTIVE_COLOR,
  SLOT_STATUS,
  buildSlotGroups,
  draftNomenclatureIds,
  mergeSlots,
  pluralSlots,
} from "@/helpers/moduleSchemeSlots";

const NOTICE_MS = 3500;
// без объекта схемы позицию подбирать нельзя (то же правило и на бэке: 422)
const NO_OBJECT_TEXT = "Сначала выберите объект схемы: без объекта позицию подбирать нельзя";

/**
 * Слоты оборудования модульной схемы: модули схемы по порядку, внутри — экземпляры
 * оборудования их чертежей («КШ-1», «Котёл отопительный»). В каждый подбирают номенклатуру по
 * условиям, заданным у узла («Оборудование» в узле схем), и количество.
 *
 * Слот — запись БД (m_to_m_module_scheme_slots): позиция + количество + статус
 * (набросок → куплено → установлено) + снимок условий узла. Запись создаётся лениво — при первом
 * выборе позиции, поэтому часть строк «виртуальная» (db = null). Купленное и установленное
 * заморожено: бэк не даёт менять позицию и количество, строка показывается с замком.
 *
 * Можно выбрать несколько слотов одного типа (чекбоксы, «Выбрать все…», Shift+клик на схеме)
 * и назначить им одну позицию сразу. Номер экземпляра — порядок чтения SVG узла: при замене
 * чертежа он может сместиться; слоты, которых на чертеже больше нет, показываются отдельно.
 */
export default {
  name: "ModuleSchemeSlots",
  components: { ModuleSchemeSlotPicker, ModuleSchemeSlotDetails },
  props: {
    schemeId: { type: Number, default: null },
    // объект схемы: оборудование закрепляется за ним; без объекта купить нельзя
    idObject: { type: Number, default: null },
    objectName: { type: String, default: "" },
    // слот под курсором — на схеме или в этом списке: { uid, key, fromSvg } или null
    hoveredSlot: { type: Object, default: null },
    // вариант сборки узла сохраняется (схема и список перезагружаются) — переключатели вариантов недоступны
    variantSaving: { type: Boolean, default: false },
  },
  data: () => ({
    loading: false,
    error: "",
    layout: null,
    // экземпляры оборудования по id узла каталога
    elementsByNode: {},
    // записи слотов из БД
    slots: [],
    names: {},
    // свёрнутые группы-модули: uid → true
    collapsed: {},
    // выбранные слоты (ref), все одного типа элемента
    selected: [],
    notice: "",
    noticeTimer: null,
    // ответ устаревает, если за время загрузки открыли другую схему
    requestId: 0,

    pickerOpen: false,
    pickerRefs: [],
    detailsOpen: false,
    detailsRef: null,
    purchaseDialog: false,
    saving: false,
  }),
  computed: {
    NO_OBJECT_TEXT() {
      return NO_OBJECT_TEXT;
    },
    // подбирать можно при выбранном объекте схемы и пока ничего не сохраняется
    canPick() {
      return !!this.idObject && !this.saving;
    },
    activeColor() {
      return ACTIVE_COLOR;
    },
    built() {
      return buildSlotGroups({
        layout: this.layout,
        elementsByNode: this.elementsByNode,
        slots: this.slots,
        names: this.names,
      });
    },
    // слоты с признаком «выбран» — по ним схема рисует рамки
    groups() {
      const picked = new Set(this.selected);
      return this.built.groups.map((group) => ({
        ...group,
        slots: group.slots.map((slot) => ({ ...slot, selected: picked.has(slot.ref) })),
      }));
    },
    orphans() {
      return this.built.orphans;
    },
    flatSlots() {
      return this.groups.reduce((all, group) => all.concat(group.slots), []);
    },
    selectedSlots() {
      return this.flatSlots.filter((slot) => slot.selected);
    },
    selectedType() {
      return this.selectedSlots.length ? this.selectedSlots[0].idElement : null;
    },
    // типы оборудования для «Выбрать все…»: считаем только слоты, которые ещё можно менять
    typeOptions() {
      const map = new Map();
      this.flatSlots.forEach((slot) => {
        if (slot.frozen) return;
        const entry = map.get(slot.idElement) || { idElement: slot.idElement, title: slot.elementName, count: 0 };
        entry.count++;
        map.set(slot.idElement, entry);
      });
      return [...map.values()];
    },
    summary() {
      const filled = this.flatSlots.filter((s) => s.chosen).length;
      const parts = [`выбрано ${filled} из ${this.flatSlots.length}`];
      ["purchased", "installed"].forEach((status) => {
        const count = this.flatSlots.filter((s) => s.status === status).length;
        if (count) parts.push(`${SLOT_STATUS[status].label.toLowerCase()} ${count}`);
      });
      return parts.join(" · ");
    },
    canClear() {
      return this.selectedSlots.some((slot) => slot.db && slot.chosen);
    },
    // «Куплено» — когда выбран объект схемы и у каждого выбранного слота записана позиция
    canPurchase() {
      return (
        !!this.idObject &&
        this.selectedSlots.length > 0 &&
        this.selectedSlots.every((slot) => slot.db && slot.chosen)
      );
    },
    purchaseHint() {
      if (this.canPurchase) return "";
      if (!this.idObject) return "Сначала выберите объект схемы: купленное закрепляется за объектом";
      return "Купить можно, когда у каждого выбранного слота есть позиция";
    },
    pickerTargets() {
      return this.pickerRefs.map((ref) => this.flatSlots.find((slot) => slot.ref === ref)).filter(Boolean);
    },
    // название объекта, за которым закреплён замороженный слот: у объекта схемы — его название, у другого — номер
    detailsObjectName() {
      const idObject = this.detailsSlot && this.detailsSlot.db ? this.detailsSlot.db.id_object : null;
      if (!idObject) return "";
      return idObject === this.idObject && this.objectName ? this.objectName : `Объект № ${idObject}`;
    },
    detailsSlot() {
      if (!this.detailsRef) return null;
      return (
        this.flatSlots.find((slot) => slot.ref === this.detailsRef) ||
        this.orphans.find((orphan) => orphan.ref === this.detailsRef) ||
        null
      );
    },
  },
  watch: {
    // схема рисует рамки по этим данным — отдаём ей список слотов целиком
    groups: {
      immediate: true,
      handler(groups) {
        this.$emit("groups", groups);
        // выбранный слот мог исчезнуть или стать купленным — убираем из выбора
        const alive = new Set(this.flatSlots.filter((s) => !s.frozen).map((s) => s.ref));
        const kept = this.selected.filter((ref) => alive.has(ref));
        if (kept.length !== this.selected.length) this.selected = kept;
      },
    },
    // навели на элемент схемы — показываем его строку, если она скрыта прокруткой
    hoveredSlot(hovered) {
      if (!hovered || !hovered.fromSvg) return;
      this.$nextTick(() => {
        const row = this.$el.querySelector(`[data-slot="${hovered.uid}:${hovered.key}"]`);
        if (row && row.offsetParent) row.scrollIntoView({ block: "nearest" });
      });
    },
    schemeId: {
      immediate: true,
      handler(id) {
        if (id) this.load(id);
        else this.reset();
      },
    },
  },
  beforeDestroy() {
    clearTimeout(this.noticeTimer);
  },
  methods: {
    pluralSlots,
    statusMeta(slot) {
      return SLOT_STATUS[slot.status] || SLOT_STATUS.draft;
    },
    // подсказка у индикатора справа: статус слота и, у наброска, подобрана ли позиция
    stateTitle(slot) {
      const label = this.statusMeta(slot).label;
      if (slot.frozen) return label;
      return `${label} · позиция ${slot.chosen ? "подобрана" : "не подобрана"}`;
    },
    reset() {
      this.requestId++;
      this.loading = false;
      this.error = "";
      this.layout = null;
      this.elementsByNode = {};
      this.slots = [];
      this.names = {};
      this.collapsed = {};
      this.selected = [];
      this.notice = "";
      this.pickerOpen = false;
      this.pickerRefs = [];
      this.detailsOpen = false;
      this.detailsRef = null;
      this.purchaseDialog = false;
    },
    // строку красим только когда элемент под курсором на схеме — наведение на саму строку
    // её целиком не красит (как в «Оборудовании» узла)
    isSlotHoveredFromSvg(uid, key) {
      const h = this.hoveredSlot;
      return !!h && h.fromSvg && h.uid === uid && h.key === key;
    },
    isCollapsed(uid) {
      return !!this.collapsed[uid];
    },
    toggleGroup(uid) {
      this.collapsed = { ...this.collapsed, [uid]: !this.collapsed[uid] };
    },
    showNotice(text) {
      this.notice = text;
      clearTimeout(this.noticeTimer);
      this.noticeTimer = setTimeout(() => (this.notice = ""), NOTICE_MS);
    },

    // --- выбор нескольких слотов ---
    isCheckDisabled(slot) {
      return this.selectedType !== null && this.selectedType !== slot.idElement && !slot.selected;
    },
    checkTitle(slot) {
      if (slot.frozen) return "Слот заморожен — менять нельзя";
      return this.isCheckDisabled(slot) ? "Массово можно выбирать оборудование только одного типа" : "";
    },
    toggleSelect(slot) {
      if (slot.frozen) return;
      if (slot.selected) {
        this.selected = this.selected.filter((ref) => ref !== slot.ref);
        return;
      }
      if (this.isCheckDisabled(slot)) {
        this.showNotice("Массово можно выбирать оборудование только одного типа");
        return;
      }
      this.selected = [...this.selected, slot.ref];
    },
    selectType(idElement) {
      this.selected = this.flatSlots.filter((s) => s.idElement === idElement && !s.frozen).map((s) => s.ref);
    },
    clearSelection() {
      this.selected = [];
    },

    // --- открытие окон ---
    openSlot(slot) {
      if (slot.frozen) {
        this.detailsRef = slot.ref;
        this.detailsOpen = true;
      } else {
        this.openPicker([slot]);
      }
    },
    openOrphan(orphan) {
      if (!orphan.frozen) return;
      this.detailsRef = orphan.ref;
      this.detailsOpen = true;
    },
    openPicker(slots) {
      // подбор открываем только при выбранном объекте схемы
      if (!this.idObject) {
        this.showNotice(NO_OBJECT_TEXT);
        return;
      }
      this.pickerRefs = slots.map((slot) => slot.ref);
      this.pickerOpen = true;
    },
    // Клик по элементу на схеме: обычный — подбор для этого слота; Shift/Ctrl/Cmd — добавить к выбору
    pickFromSvg({ uid, key, multi }) {
      const slot = this.flatSlots.find((s) => s.uid === uid && s.key === key);
      if (!slot) return;
      if (multi) this.toggleSelect(slot);
      else this.openSlot(slot);
    },

    // --- загрузка ---
    async load(id) {
      this.reset();
      const requestId = this.requestId;
      this.loading = true;

      const data = await this.$store.dispatch("ModuleSchemesModule/loadSlots", id);
      if (requestId !== this.requestId) return;
      this.loading = false;
      if (!data) {
        this.error = "Не удалось загрузить оборудование схемы";
        return;
      }

      const byNode = {};
      (data.nodes || []).forEach((node) => {
        byNode[node.id] = {
          code: node.code,
          elements: node._detected_elements || [],
          variants: node._detected_variants || [1],
          functionalities: node._functionalities || [],
        };
      });
      this.elementsByNode = byNode;
      this.layout = data.layout;
      this.slots = data.slots;
      await this.loadNames();
    },
    // Названия наброска по каталогу — одним запросом, только недостающие (у купленных есть снимок)
    async loadNames() {
      const missing = [...new Set(draftNomenclatureIds(this.slots))].filter((id) => !this.names[id]);
      if (!missing.length) return;
      const requestId = this.requestId;
      const names = await this.$store.dispatch("ModuleSchemesModule/loadNomenclatureNames", missing);
      if (requestId !== this.requestId) return;
      this.names = { ...this.names, ...names };
    },

    // --- запись ---
    // Присоединяет позицию (и количество) к слотам: «виртуальным» создаёт записи, существующим
    // обновляет. Возвращает true, если всё записалось.
    async applyChoice(targets, { idNomenclature, quantity, name }) {
      this.saving = true;
      let updated = [];
      try {
        const virtual = targets.filter((t) => !t.db);
        const persisted = targets.filter((t) => t.db);

        if (virtual.length) {
          const items = virtual.map((t) => ({
            node_uid: t.uid,
            id_scheme_node_element: t.idElement,
            element_instance: t.n,
            id_nomenclature: idNomenclature,
            ...(quantity !== null ? { quantity } : {}),
          }));
          const created = await this.$store.dispatch("ModuleSchemesModule/createSlots", { id: this.schemeId, items });
          if (!created) return false;
          updated = updated.concat(created);
          // запись по адресу уже была (создали параллельно) — бэк вернул её как есть, выбор дописываем
          const stale = created.filter((slot) => slot.id_nomenclature !== idNomenclature);
          if (stale.length) persisted.push(...stale.map((slot) => ({ db: slot })));
        }

        if (persisted.length) {
          const changes = { id_nomenclature: idNomenclature, ...(quantity !== null ? { quantity } : {}) };
          const result = await this.$store.dispatch("ModuleSchemesModule/updateSlots", {
            id: this.schemeId,
            ids: persisted.map((t) => t.db.id),
            changes,
          });
          if (!result) return false;
          updated = updated.concat(result);
        }

        if (name) this.names = { ...this.names, [idNomenclature]: name };
        return true;
      } finally {
        // что успели записать — показываем, даже если следующий шаг не прошёл
        if (updated.length) this.slots = mergeSlots(this.slots, updated);
        this.saving = false;
      }
    },
    async onAttach(payload) {
      const ok = await this.applyChoice(this.pickerTargets, payload);
      if (ok) {
        this.pickerOpen = false;
        this.clearSelection();
      }
    },
    // Снять выбор позиции у слотов (записи остаются — со своими условиями и количеством)
    async clearChoices(targets) {
      const persisted = targets.filter((t) => t.db && t.chosen);
      if (!persisted.length) return true;
      this.saving = true;
      try {
        const result = await this.$store.dispatch("ModuleSchemesModule/updateSlots", {
          id: this.schemeId,
          ids: persisted.map((t) => t.db.id),
          changes: { id_nomenclature: null },
        });
        if (!result) return false;
        this.slots = mergeSlots(this.slots, result);
        return true;
      } finally {
        this.saving = false;
      }
    },
    async onClear() {
      if (await this.clearChoices(this.pickerTargets)) this.pickerOpen = false;
    },
    clearSelectedChoices() {
      return this.clearChoices(this.selectedSlots);
    },
    async purchase() {
      const ids = this.selectedSlots.map((slot) => slot.db.id);
      this.saving = true;
      try {
        const result = await this.$store.dispatch("ModuleSchemesModule/purchaseSlots", { id: this.schemeId, ids });
        if (!result) return;
        this.slots = mergeSlots(this.slots, result);
        this.purchaseDialog = false;
        this.clearSelection();
      } finally {
        this.saving = false;
      }
    },
    async deleteOrphan(orphan) {
      this.saving = true;
      try {
        const ok = await this.$store.dispatch("ModuleSchemesModule/deleteSlot", {
          id: this.schemeId,
          idSlot: orphan.db.id,
        });
        if (ok) this.slots = this.slots.filter((slot) => slot.id !== orphan.db.id);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.module_scheme_slots {
  // активный цвет (ACTIVE_COLOR в helpers/moduleSchemeSlots.js): выделенные слоты, панель действий,
  // слот под курсором на схеме — тот же фиолетовый, что и рамка на схеме
  --slot-active: #7b1fa2;
  --slot-active-rgb: 123, 31, 162;
  // блок занимает всё, что осталось в колонке до низа предпросмотра схемы
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  &__bar {
    display: flex;
    align-items: baseline;
    margin-bottom: 4px;
  }
  &__info {
    font-size: 0.8em;
    color: rgba(0, 0, 0, 0.6);
  }
  &__object {
    margin-bottom: 6px;
    font-size: 0.8em;
    color: #bf5b00;
  }
  &__state {
    padding: 16px 0;
    font-size: 0.85em;
    color: rgba(0, 0, 0, 0.6);
  }
  &__tools {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
  }
  &__hint {
    font-size: 0.72em;
    color: rgba(0, 0, 0, 0.5);
    &--notice {
      color: #bf0022;
    }
  }
  &__actions {
    margin-bottom: 8px;
    padding: 6px 8px;
    border: 1px solid rgba(var(--slot-active-rgb), 0.4);
    border-radius: 6px;
    background: rgba(var(--slot-active-rgb), 0.06);
  }
  &__actions_head {
    display: flex;
    align-items: center;
  }
  &__actions_buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 4px;
    margin-top: 4px;
  }
  &__actions_text {
    font-size: 0.85em;
  }
  &__list {
    // basis 0: высоту задаёт колонка (она тянется до низа схемы), а не число слотов;
    // на узком экране, где колонки друг под другом, список не ниже 240px
    flex: 1 1 0;
    min-height: 240px;
    overflow-y: auto;
  }
  &__group {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 4px 4px 4px 0;
    border-radius: 4px;
    font-size: 0.75em;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.6);
    &:hover {
      background: rgba(0, 0, 0, 0.04);
    }
    &--static {
      padding-left: 4px;
      &:hover {
        background: none;
      }
    }
  }
  // кнопка сворачивания внутри строки заголовка группы: занимает всё свободное место слева от переключателя вариантов
  &__group_toggle {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    text-align: left;
    font: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    color: inherit;
  }
  &__variants {
    flex: none;
    ::v-deep .v-btn {
      min-width: 24px !important;
      height: 22px !important;
      padding: 0 !important;
      font-weight: 600;
    }
  }
  &__group_count {
    font-weight: 400;
    letter-spacing: 0;
    &--done {
      color: #2e9e5b;
      font-weight: 600;
    }
  }
  &__slot {
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    margin-bottom: 4px;
    padding: 2px 8px 2px 7px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 6px;
    // полоска слева цвета состояния (--state-color) — те же цвета, что у рамок на схеме
    &::before {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: 3px;
      background: var(--state-color, transparent);
    }
    &:hover {
      background: rgba(0, 0, 0, 0.04);
    }
    &--empty {
      border-style: dashed;
    }
    // подобрано (набросок с позицией) — зелёный, куплено — оранжевый, установлено — бирюзовый
    &--draft {
      --state-color: #2e9e5b;
      background: rgba(46, 158, 91, 0.07);
    }
    &--purchased {
      --state-color: #ef6c00;
      background: rgba(239, 108, 0, 0.07);
    }
    &--installed {
      --state-color: #00897b;
      background: rgba(0, 137, 123, 0.07);
    }
    &--selected {
      border-color: var(--slot-active);
      background: rgba(var(--slot-active-rgb), 0.08);
    }
    &--orphan {
      border-color: rgba(239, 108, 0, 0.5);
    }
    // элемент под курсором на схеме — тот же активный цвет, что и его рамка
    &--hovered {
      background: rgba(var(--slot-active-rgb), 0.08);
    }
  }
  &__state {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: flex-end;
    margin-left: 4px;
  }
  &__check {
    display: inline-flex;
    flex: 0 0 28px;
    align-items: center;
    justify-content: center;
    height: 28px;
  }
  &__slot_main {
    display: flex;
    flex: 1;
    align-items: center;
    min-width: 0;
    padding: 4px 4px 4px 0;
    text-align: left;
  }
  &__slot_text {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
  }
  &__slot_label {
    font-size: 0.9em;
    font-weight: 600;
  }
  &__slot_value {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.8em;
    color: rgba(0, 0, 0, 0.6);
    &--empty {
      font-style: italic;
    }
  }
  &__qty {
    margin-left: 4px;
    color: rgba(0, 0, 0, 0.8);
  }
}
</style>
